import type {
  Activity,
  AuthQrChallengeCreateResponse,
  AuthQrChallengePollResponse,
  AuthResponse,
  Comment,
  CommentAnchor,
  DiscoverBbox,
  DiscoverRouteListItem,
  DiscoverRouteListResponse,
  Difficulty,
  Favorite,
  FavoriteWithRoute,
  GeometryResponse,
  InboxResponse,
  InboxSeenRequest,
  InboxUnreadCountResponse,
  MediaAssetPublic,
  NaturalFeatures,
  ProposalPublic,
  ProposalCreateRequest,
  ProposalRejectRequest,
  Region,
  RevisionPublic,
  RouteCreateRequest,
  RouteEnrichmentResult,
  RouteFromGeometryRequest,
  RouteFromGeometryResponse,
  RouteListItem,
  RoutePatchRequest,
  RoutePointInput,
  RoutePublic,
  RouteStatus,
  RouteType,
  RoutingPreviewRequest,
  RoutingPreviewResponse,
  SavedSearch,
  TitleCandidate,
  UserPublic,
  Watch,
  Waypoint,
  WaypointsReplaceRequest,
  AdminUserListResponse,
  AdminUserStatus,
} from '@xingroute/schema'
import { parseMediaProxyPath } from '@xingroute/schema'
import { normalizeGeometry } from '../lib/routeGeometry.js'

type MyRouteStatus = Exclude<RoutePublic['status'], 'archived'>

export type { RouteEnrichmentResult, TitleCandidate } from '@xingroute/schema'

export interface RegionTreeArea {
  name: string
  count: number
  kind: 'mountain' | 'district' | 'unknown'
}

export interface RegionTreeCity {
  name: string
  count: number
  areas: RegionTreeArea[]
}

export interface RegionTreeProvince {
  name: string
  count: number
  cities: RegionTreeCity[]
}

type RouteRichEnrichBrief = {
  title: string
  current_summary?: string
  stats?: RouteEnrichmentResult['stats']
  region?: RouteEnrichmentResult['region']
  title_candidates?: TitleCandidate[]
  natural_features?: RoutePublic['natural_features']
  activities?: RoutePublic['activities']
}

type RouteRichEnrichResponse = {
  summary?: string
  description_md?: string
  title_candidate?: TitleCandidate
  difficulty?: RoutePublic['difficulty']
  suggested_distance_km?: number
}

export interface ContributionListItem {
  id: string
  route_id?: string
  route_slug?: string
  route_title?: string
  action?: string
  created_at?: string
}

type ContributionListResponse = { items: ContributionListItem[] } | ContributionListItem[]

// Minimal typed fetch client. Keeps dependencies thin; replace with a richer
// caching layer when request volume needs it.
//
// All requests go through the Vite dev proxy at /api in dev, and the same
// origin in production (frontend + API share xingroute.com via Caddy
// reverse_proxy) — so `credentials: 'include'` is enough for the refresh
// cookie to ride along.

const BASE = '/api'
const DEFAULT_REQUEST_TIMEOUT_MS = 30_000

type AuthSessionListener = (session: AuthResponse | null, originToken?: string) => void

const authSessionListeners = new Set<AuthSessionListener>()
let refreshInFlight: Promise<AuthResponse> | null = null
const mediaSessionRecoveryInFlight = new Map<string, Promise<AuthResponse | null>>()

export function subscribeAuthSession(listener: AuthSessionListener): () => void {
  authSessionListeners.add(listener)
  return () => authSessionListeners.delete(listener)
}

function notifyAuthSession(session: AuthResponse | null, originToken?: string): void {
  for (const listener of authSessionListeners) listener(session, originToken)
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

interface Options {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  formData?: FormData
  token?: string | null
  ifMatch?: string
  timeoutMs?: number
  skipAuthRefresh?: boolean
}

function resolveMediaProxyUrl(source: string): URL {
  const base = new URL(globalThis.location.href)
  const url = new URL(source, base)
  if (
    url.origin !== base.origin ||
    url.username !== '' ||
    url.password !== '' ||
    url.hash !== '' ||
    parseMediaProxyPath(url.pathname) == null
  ) {
    throw new ApiError(0, 'unsafe_media_url', 'Media URL must use the same-origin media proxy')
  }
  return url
}

async function call<T>(
  path: string,
  opts: Options = {},
): Promise<{ data: T; etag: string | null }> {
  return callOnce<T>(path, opts, false)
}

async function callOnce<T>(
  path: string,
  opts: Options,
  didRetryAuth: boolean,
): Promise<{ data: T; etag: string | null }> {
  const headers: Record<string, string> = {}
  let body: BodyInit | undefined
  if (opts.formData) {
    body = opts.formData
  } else if (opts.body !== undefined) {
    headers['content-type'] = 'application/json'
    body = JSON.stringify(opts.body)
  }
  if (opts.token) headers['authorization'] = `Bearer ${opts.token}`
  if (opts.ifMatch) headers['if-match'] = opts.ifMatch

  const res = await fetchWithTimeout(`${BASE}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    body,
    credentials: 'include',
    timeoutMs: opts.timeoutMs,
  })
  // Caddy `encode` appends the content-encoding name to the ETag opaque-tag
  // (e.g., "abc123" → "abc123-zstd"). Strip the suffix so If-Match sends the
  // canonical value the server expects.
  const rawEtag = res.headers.get('etag')
  const etag = rawEtag
    ? rawEtag
        .replace(/^W\//, '')
        .replace(/"/g, '')
        .replace(/-(zstd|gzip|br|deflate)$/, '') || null
    : null

  if (res.status === 204) {
    return { data: undefined as T, etag }
  }
  const text = await res.text()
  let json: unknown = null
  let parseError: unknown = null
  if (text) {
    try {
      json = JSON.parse(text)
    } catch (err) {
      parseError = err
    }
  }
  if (!res.ok) {
    if (res.status === 401 && opts.token && !opts.skipAuthRefresh && !didRetryAuth) {
      try {
        const refreshed = await refreshAccessToken(opts.timeoutMs)
        notifyAuthSession(refreshed, opts.token)
        return callOnce<T>(path, { ...opts, token: refreshed.access_token }, true)
      } catch (refreshErr) {
        if (refreshErr instanceof ApiError && refreshErr.status === 401) {
          notifyAuthSession(null, opts.token)
        } else throw refreshErr
      }
    }
    // Non-2xx responses are sometimes HTML (e.g. Hono's default 404 page when
    // a route is genuinely missing). Surface a typed ApiError instead of
    // letting JSON.parse blow up the caller — the contributions tab in
    // MePage explicitly branches on status === 404 to fall back to an
    // EmptyState ("warming up").
    const err =
      parseError == null && json != null ? (json as { error?: string; message?: string }) : null
    const fallbackMessage = text.trim() || res.statusText || `HTTP ${res.status}`
    throw new ApiError(res.status, err?.error ?? 'http_error', err?.message || fallbackMessage)
  }
  if (parseError) {
    // 2xx with an unparsable body is a backend bug; bubble it up with a
    // typed ApiError rather than a raw SyntaxError so callers can treat it
    // uniformly with HTTP errors.
    throw new ApiError(
      res.status,
      'invalid_response',
      parseError instanceof Error ? parseError.message : String(parseError),
    )
  }
  return { data: json as T, etag }
}

async function refreshAccessToken(timeoutMs: number | undefined): Promise<AuthResponse> {
  refreshInFlight ??= call<AuthResponse>('/auth/refresh', {
    method: 'POST',
    timeoutMs,
    skipAuthRefresh: true,
  })
    .then(({ data }) => data)
    .finally(() => {
      refreshInFlight = null
    })
  return refreshInFlight
}

async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit & { timeoutMs?: number },
): Promise<Response> {
  const timeoutMs = init.timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS
  const controller = new AbortController()
  const callerSignal = init.signal
  const abortFromCaller = () => controller.abort(callerSignal?.reason)
  if (callerSignal?.aborted) abortFromCaller()
  else callerSignal?.addEventListener('abort', abortFromCaller, { once: true })
  const timeout = setTimeout(() => controller.abort(), timeoutMs)
  try {
    return await fetch(input, { ...init, signal: controller.signal })
  } catch (err) {
    if (callerSignal?.aborted) {
      throw callerSignal.reason instanceof Error
        ? callerSignal.reason
        : new DOMException('The operation was aborted', 'AbortError')
    }
    if (controller.signal.aborted) {
      throw new ApiError(0, 'request_timeout', `Request timed out after ${timeoutMs} ms`)
    }
    throw err
  } finally {
    clearTimeout(timeout)
    callerSignal?.removeEventListener('abort', abortFromCaller)
  }
}

type MediaFetchResult =
  | { ok: true; blob: Blob }
  | {
      ok: false
      status: number
      error: { error?: string; message?: string } | null
      statusText: string
    }

async function fetchMediaAttempt(
  source: string,
  token: string | null | undefined,
  signal: AbortSignal,
): Promise<MediaFetchResult> {
  const url = resolveMediaProxyUrl(source)
  const headers: Record<string, string> = { accept: 'image/*' }
  if (token) headers.authorization = `Bearer ${token}`
  const controller = new AbortController()
  let timedOut = false
  const abortFromCaller = () => controller.abort(signal.reason)
  if (signal.aborted) abortFromCaller()
  else signal.addEventListener('abort', abortFromCaller, { once: true })
  const timeout = setTimeout(() => {
    timedOut = true
    controller.abort()
  }, DEFAULT_REQUEST_TIMEOUT_MS)
  try {
    const res = await fetch(url, {
      headers,
      credentials: 'include',
      cache: 'no-store',
      redirect: 'error',
      signal: controller.signal,
    })
    if (res.ok) {
      const contentType = res.headers.get('content-type')?.toLowerCase() ?? ''
      if (!contentType.startsWith('image/')) {
        throw new ApiError(res.status, 'invalid_media_response', 'Media response is not an image')
      }
      return { ok: true, blob: await res.blob() }
    }
    const text = await res.text()
    return {
      ok: false,
      status: res.status,
      error: parseApiErrorText(text),
      statusText: res.statusText,
    }
  } catch (error) {
    if (signal.aborted) {
      throw signal.reason instanceof Error
        ? signal.reason
        : new DOMException('The operation was aborted', 'AbortError')
    }
    if (timedOut) {
      throw new ApiError(
        0,
        'request_timeout',
        `Request timed out after ${DEFAULT_REQUEST_TIMEOUT_MS} ms`,
      )
    }
    throw error
  } finally {
    clearTimeout(timeout)
    signal.removeEventListener('abort', abortFromCaller)
  }
}

async function recoverMediaSessionAfterNotFound(token: string): Promise<AuthResponse | null> {
  const existing = mediaSessionRecoveryInFlight.get(token)
  if (existing) return existing
  const recovery = (async () => {
    try {
      await call<UserPublic>('/me', { token, skipAuthRefresh: true })
      return null
    } catch (error) {
      if (!(error instanceof ApiError && error.status === 401)) throw error
    }
    try {
      const session = await refreshAccessToken(undefined)
      notifyAuthSession(session, token)
      return session
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        notifyAuthSession(null, token)
        return null
      }
      throw error
    }
  })().finally(() => {
    if (mediaSessionRecoveryInFlight.get(token) === recovery) {
      mediaSessionRecoveryInFlight.delete(token)
    }
  })
  mediaSessionRecoveryInFlight.set(token, recovery)
  return recovery
}

function throwIfAborted(signal: AbortSignal): void {
  if (!signal.aborted) return
  throw signal.reason instanceof Error
    ? signal.reason
    : new DOMException('The operation was aborted', 'AbortError')
}

async function fetchMediaBlobWithAuth(
  source: string,
  token: string | null | undefined,
  signal: AbortSignal,
  didRetryAuth = false,
): Promise<Blob> {
  const result = await fetchMediaAttempt(source, token, signal)
  if (result.ok) return result.blob
  if (result.status === 401 && token && !didRetryAuth) {
    try {
      const refreshed = await refreshAccessToken(undefined)
      notifyAuthSession(refreshed, token)
      throwIfAborted(signal)
      return fetchMediaBlobWithAuth(source, refreshed.access_token, signal, true)
    } catch (refreshErr) {
      if (refreshErr instanceof ApiError && refreshErr.status === 401) {
        notifyAuthSession(null, token)
      } else {
        throw refreshErr
      }
    }
  }
  if (
    result.status === 404 &&
    result.error?.error === 'not_found' &&
    result.error.message === 'route not found' &&
    token &&
    !didRetryAuth
  ) {
    const refreshed = await recoverMediaSessionAfterNotFound(token)
    throwIfAborted(signal)
    if (refreshed) {
      return fetchMediaBlobWithAuth(source, refreshed.access_token, signal, true)
    }
  }
  throw new ApiError(
    result.status,
    result.error?.error ?? 'http_error',
    result.error?.message ?? result.statusText,
  )
}

async function fetchTextWithAuth(
  path: string,
  token: string,
  timeoutMs: number | undefined,
  didRetryAuth = false,
  headersInit: Record<string, string> = {},
): Promise<string> {
  const res = await fetchWithTimeout(`${BASE}${path}`, {
    headers: { ...headersInit, authorization: `Bearer ${token}` },
    credentials: 'include',
    cache: 'no-store',
    timeoutMs,
  })
  const text = await res.text()
  if (res.ok) return text
  if (res.status === 401 && !didRetryAuth) {
    try {
      const refreshed = await refreshAccessToken(timeoutMs)
      notifyAuthSession(refreshed, token)
      return fetchTextWithAuth(path, refreshed.access_token, timeoutMs, true, headersInit)
    } catch (refreshErr) {
      if (refreshErr instanceof ApiError && refreshErr.status === 401)
        notifyAuthSession(null, token)
      else throw refreshErr
    }
  }
  const err = parseApiErrorText(text)
  throw new ApiError(res.status, err?.error ?? 'http_error', err?.message ?? res.statusText)
}

function parseApiErrorText(text: string): { error?: string; message?: string } | null {
  try {
    return text ? (JSON.parse(text) as { error?: string; message?: string }) : null
  } catch {
    return null
  }
}

export const api = {
  async register(username: string, password: string, displayName?: string) {
    const { data } = await call<AuthResponse>('/auth/password/register', {
      method: 'POST',
      body: { username, password, display_name: displayName },
    })
    return data
  },
  async login(username: string, password: string) {
    const { data } = await call<AuthResponse>('/auth/password/login', {
      method: 'POST',
      body: { username, password },
    })
    return data
  },
  async createQrLoginChallenge() {
    const { data } = await call<AuthQrChallengeCreateResponse>('/auth/qr/challenges', {
      method: 'POST',
    })
    return data
  },
  async pollQrLoginChallenge(challengeId: string, secret: string) {
    const { data } = await call<AuthQrChallengePollResponse>(
      `/auth/qr/challenges/${encodeURIComponent(challengeId)}/poll`,
      {
        method: 'POST',
        body: { secret },
      },
    )
    return data
  },
  async refresh() {
    const { data } = await call<AuthResponse>('/auth/refresh', { method: 'POST' })
    return data
  },
  async logout() {
    await call<{ ok: true }>('/auth/logout', { method: 'POST' })
  },
  async me(token: string, opts: { skipAuthRefresh?: boolean } = {}) {
    const { data } = await call<UserPublic>('/me', {
      token,
      skipAuthRefresh: opts.skipAuthRefresh,
    })
    return data
  },
  async listAdminUsers(
    opts: {
      page: number
      pageSize: number
      search?: string
      status?: AdminUserStatus
    },
    token: string,
  ) {
    const qs = new URLSearchParams({
      page: String(opts.page),
      page_size: String(opts.pageSize),
    })
    if (opts.search) qs.set('search', opts.search)
    if (opts.status) qs.set('status', opts.status)
    const { data } = await call<AdminUserListResponse>(`/admin/users?${qs.toString()}`, { token })
    return data
  },
  async listRoutes(
    opts: {
      status?: RouteStatus
      bbox?: DiscoverBbox
      limit?: number
      includeSeasonality?: boolean
      includeThumbnail?: boolean
      activity?: Activity
      difficulty?: Difficulty
      routeType?: RouteType
      region?: string
      sort?: 'recent' | 'distance' | 'ascent'
    } = {},
  ) {
    const data = await this.listRoutePage(opts)
    return data.items
  },
  async listRoutePage(
    opts: {
      status?: RouteStatus
      bbox?: DiscoverBbox
      limit?: number
      cursor?: string
      includeSeasonality?: boolean
      includeThumbnail?: boolean
      activity?: Activity
      difficulty?: Difficulty
      routeType?: RouteType
      region?: string
      sort?: 'recent' | 'distance' | 'ascent'
    } = {},
  ) {
    const qs = new URLSearchParams()
    if (opts.status) qs.set('status', opts.status)
    if (opts.bbox) qs.set('bbox', opts.bbox.join(','))
    if (opts.limit) qs.set('limit', String(opts.limit))
    if (opts.cursor) qs.set('cursor', opts.cursor)
    if (opts.activity) qs.set('activity', opts.activity)
    if (opts.difficulty) qs.set('difficulty', opts.difficulty)
    if (opts.routeType) qs.set('route_type', opts.routeType)
    if (opts.region?.trim()) qs.set('region', opts.region.trim())
    if (opts.sort) qs.set('sort', opts.sort)
    const include = [
      opts.includeSeasonality ? 'seasonality' : null,
      opts.includeThumbnail ? 'thumbnail' : null,
    ].filter((value): value is string => Boolean(value))
    if (include.length > 0) qs.set('include', include.join(','))
    const suffix = qs.size ? `?${qs.toString()}` : ''
    const { data } = await call<DiscoverRouteListResponse>(`/routes${suffix}`)
    return data
  },
  async getRegionTree() {
    const { data } = await call<{ provinces: RegionTreeProvince[] }>('/regions/tree')
    return data
  },
  async getRoute(id: string, token?: string | null) {
    const { data, etag } = await call<RoutePublic>(`/routes/${id}`, { token })
    return { route: data, etag }
  },
  async getRouteBySlug(slug: string, token?: string | null) {
    const { data, etag } = await call<RoutePublic>(`/routes/by-slug/${encodeURIComponent(slug)}`, {
      token,
    })
    return { route: data, etag }
  },
  async createRoute(body: RouteCreateRequest, token: string) {
    const { data, etag } = await call<RoutePublic>('/routes', { method: 'POST', body, token })
    return { route: data, etag }
  },
  async createRouteFromGpx(
    file: File,
    opts: {
      activity?: Activity
      title?: string
      summary?: string
      difficulty?: 'easy' | 'moderate' | 'hard' | 'expert'
      status?: 'draft'
    },
    token: string,
  ) {
    const fd = new FormData()
    fd.append('file', file)
    if (opts.activity) fd.append('activity', opts.activity)
    if (opts.title) fd.append('title', opts.title)
    if (opts.summary !== undefined) fd.append('summary', opts.summary)
    if (opts.difficulty) fd.append('difficulty', opts.difficulty)
    if (opts.status) fd.append('status', opts.status)
    const { data, etag } = await call<
      | RoutePublic
      | ({ route: RoutePublic; enrich?: RouteEnrichmentResult | null } & Partial<RoutePublic>)
    >('/routes/from-gpx', {
      method: 'POST',
      formData: fd,
      token,
    })
    const route = 'route' in data && data.route ? data.route : (data as RoutePublic)
    const enrich = 'enrich' in data ? (data.enrich ?? null) : null
    return { route, etag, enrich }
  },
  async createRouteFromGeometry(body: RouteFromGeometryRequest, token: string) {
    const { data, etag } = await call<RouteFromGeometryResponse>('/routes/from-geometry', {
      method: 'POST',
      body,
      token,
    })
    return {
      route: data.route,
      geometry: normalizeGeometry(data.geometry as GeometryResponse),
      etag: data.etag ?? etag,
      enrich: data.enrich ?? null,
    }
  },
  async previewRoute(body: RoutingPreviewRequest, token: string) {
    const { data } = await call<RoutingPreviewResponse>('/routing/preview', {
      method: 'POST',
      body,
      token,
    })
    return data
  },
  async previewRouteFromGpx(
    file: File,
    opts: { activity?: Activity; title?: string },
    token: string,
  ) {
    const fd = new FormData()
    fd.append('file', file)
    if (opts.activity) fd.append('activity', opts.activity)
    if (opts.title) fd.append('title', opts.title)
    const { data } = await call<{
      enrich: RouteEnrichmentResult
      suggested_difficulty?: 'easy' | 'moderate' | 'hard' | 'expert'
    }>('/routes/preview-from-gpx', { method: 'POST', formData: fd, token })
    return data
  },
  // 重新识别 — editor calls this with the route's geometry centroid to ask
  // Tianditu to refresh `region` (province/city/district + best peak). Used
  // for already-saved routes where the original auto-detect missed.
  async reverseGeocode(point: { lat: number; lon: number }, token: string) {
    const params = new URLSearchParams({
      lat: String(point.lat),
      lon: String(point.lon),
    })
    const { data } = await call<{ region: Region | null; natural_features: NaturalFeatures }>(
      `/regions/reverse?${params.toString()}`,
      { method: 'GET', token },
    )
    return data
  },
  async patchRoute(id: string, body: RoutePatchRequest, token: string, ifMatch: string) {
    const { data, etag } = await call<RoutePublic>(`/routes/${id}`, {
      method: 'PATCH',
      body,
      token,
      ifMatch,
    })
    return { route: data, etag }
  },
  async requestRoutePublish(id: string, token: string, ifMatch: string) {
    const { data, etag } = await call<RoutePublic>(`/routes/${id}/request-publish`, {
      method: 'POST',
      body: {
        accepted: true,
        license_version: '2026-08-24',
        accepted_etag: ifMatch,
      },
      token,
      ifMatch,
    })
    return { route: data, etag }
  },
  async approveRoutePublish(id: string, token: string, ifMatch: string) {
    const { data, etag } = await call<RoutePublic>(`/routes/${id}/approve-publish`, {
      method: 'POST',
      token,
      ifMatch,
    })
    return { route: data, etag }
  },
  async rejectRoutePublish(id: string, token: string, ifMatch: string) {
    const { data, etag } = await call<RoutePublic>(`/routes/${id}/reject-publish`, {
      method: 'POST',
      token,
      ifMatch,
    })
    return { route: data, etag }
  },
  async archiveRoute(id: string, token: string, ifMatch: string) {
    const { data, etag } = await call<RoutePublic>(`/routes/${id}/archive`, {
      method: 'POST',
      token,
      ifMatch,
    })
    return { route: data, etag }
  },
  async restoreRoute(id: string, token: string, ifMatch: string) {
    const { data, etag } = await call<RoutePublic>(`/routes/${id}/restore`, {
      method: 'POST',
      token,
      ifMatch,
    })
    return { route: data, etag }
  },
  async cancelPublish(id: string, token: string, ifMatch: string) {
    const { data, etag } = await call<RoutePublic>(`/routes/${id}/cancel-publish`, {
      method: 'POST',
      token,
      ifMatch,
    })
    return { route: data, etag }
  },
  async deleteRoute(id: string, token: string, ifMatch: string) {
    await call<void>(`/routes/${id}`, { method: 'DELETE', token, ifMatch })
  },
  async uploadRouteGeometry(id: string, file: File, token: string, ifMatch: string) {
    const fd = new FormData()
    fd.append('file', file)
    const { data, etag } = await call<RoutePublic>(`/routes/${id}/geometry`, {
      method: 'POST',
      formData: fd,
      token,
      ifMatch,
    })
    return { route: data, etag }
  },
  async deleteRouteGeometry(id: string, token: string, ifMatch: string) {
    const { data, etag } = await call<RoutePublic>(`/routes/${id}/geometry`, {
      method: 'DELETE',
      token,
      ifMatch,
    })
    return { route: data, etag }
  },
  async getRouteGeometry(id: string, token?: string | null) {
    const { data, etag } = await call<GeometryResponse>(`/routes/${id}/geometry`, { token })
    const geometry = normalizeGeometry(data)
    return { geometry, etag: etag ?? geometry.etag }
  },
  async replaceRoutePoints(id: string, points: RoutePointInput[], token: string, ifMatch: string) {
    const { data, etag } = await call<GeometryResponse>(`/routes/${id}/points`, {
      method: 'PUT',
      body: { points },
      token,
      ifMatch,
    })
    const geometry = normalizeGeometry(data)
    return { geometry, etag: etag ?? geometry.etag }
  },
  async getRouteGpxText(id: string, token?: string | null, profile: 'mobile' | 'raw' = 'mobile') {
    const path = `/routes/${id}/geometry${profile === 'raw' ? '?profile=raw' : ''}`
    if (token) {
      return fetchTextWithAuth(path, token, undefined, false, {
        accept: 'application/gpx+xml',
      })
    }
    const res = await fetchWithTimeout(`${BASE}${path}`, {
      headers: { accept: 'application/gpx+xml' },
      credentials: 'include',
      cache: 'no-store',
    })
    const text = await res.text()
    if (res.ok) return text
    const err = parseApiErrorText(text)
    throw new ApiError(res.status, err?.error ?? 'http_error', err?.message ?? res.statusText)
  },
  async createFavorite(routeId: string, token: string) {
    const { data } = await call<Favorite>('/favorites', {
      method: 'POST',
      body: { route_id: routeId },
      token,
    })
    return data
  },
  async deleteFavorite(routeId: string, token: string) {
    await call<{ ok: true }>(`/favorites/${encodeURIComponent(routeId)}`, {
      method: 'DELETE',
      token,
    })
  },
  async listFavorites(token: string) {
    const { data } = await call<FavoriteWithRoute[]>('/me/favorites', { token })
    return data
  },
  async putRouteWaypoints(
    id: string,
    waypoints: WaypointsReplaceRequest['waypoints'],
    token: string,
    ifMatch: string,
  ) {
    const routePoints = waypoints.map(
      ({ auto: _auto, distance_along_line: _line, distance_along_track: _track, ...point }) =>
        point,
    )
    const { geometry, etag } = await api.replaceRoutePoints(id, routePoints, token, ifMatch)
    return { etag, waypoints: geometry.points }
  },
  async previewAutoWaypoints(id: string, token: string) {
    const { data } = await call<
      | WaypointsReplaceRequest['waypoints']
      | {
          waypoints?: WaypointsReplaceRequest['waypoints']
          candidates?: WaypointsReplaceRequest['waypoints']
        }
    >(`/routes/${id}/auto-waypoints?dryRun=1`, { method: 'POST', token })
    if (Array.isArray(data)) return data
    return data.waypoints ?? data.candidates ?? []
  },
  async translateWaypoints(id: string, token: string, ifMatch: string) {
    const { data, etag } = await call<GeometryResponse>(`/routes/${id}/waypoints/translate`, {
      method: 'POST',
      token,
      ifMatch,
    })
    return { geometry: data, etag: etag ?? data.etag }
  },
  async enrichRoute(title: string, currentSummary: string | undefined, token: string) {
    const { data } = await call<{
      summary?: string
      difficulty?: 'easy' | 'moderate' | 'hard' | 'expert'
      suggested_distance_km?: number
    }>('/ai/route/enrich', {
      method: 'POST',
      body: { title, current_summary: currentSummary },
      token,
    })
    return data
  },
  async enrichRouteRich(brief: RouteRichEnrichBrief, token: string) {
    const { data } = await call<RouteRichEnrichResponse>('/ai/route/enrich-rich', {
      method: 'POST',
      body: {
        title: brief.title,
        current_summary: brief.current_summary,
        stats: brief.stats,
        region: brief.region,
        title_candidates: brief.title_candidates,
        natural_features: brief.natural_features,
        activities: brief.activities,
      },
      token,
    })
    return data
  },
  async generateRouteCover(id: string, token: string, ifMatch: string) {
    const { data, etag } = await call<RoutePublic>(`/ai/route/${id}/cover-image`, {
      method: 'POST',
      token,
      ifMatch,
    })
    return { route: data, etag }
  },

  async listMedia(routeId: string, opts?: { waypointId?: string | 'none' }, token?: string | null) {
    const params = new URLSearchParams({ route_id: routeId })
    if (opts?.waypointId !== undefined) params.set('waypoint_id', opts.waypointId)
    const { data } = await call<{ items: MediaAssetPublic[] }>(`/media?${params.toString()}`, {
      token,
    })
    return data.items
  },
  async getMediaImage(source: string, token: string | null | undefined, signal: AbortSignal) {
    return fetchMediaBlobWithAuth(source, token, signal)
  },
  async uploadMedia(
    routeId: string,
    file: File,
    token: string,
    opts?: { altText?: string; waypointId?: string },
  ) {
    const fd = new FormData()
    fd.append('route_id', routeId)
    fd.append('file', file)
    if (opts?.altText) fd.append('alt_text', opts.altText)
    if (opts?.waypointId) fd.append('waypoint_id', opts.waypointId)
    const { data } = await call<MediaAssetPublic>('/media', { method: 'POST', formData: fd, token })
    return data
  },
  async uploadAvatar(file: File, token: string): Promise<AuthResponse> {
    const fd = new FormData()
    fd.append('file', file)
    const { data } = await call<AuthResponse>('/me/avatar', { method: 'PUT', formData: fd, token })
    return data
  },
  async deleteAvatar(token: string): Promise<AuthResponse> {
    const { data } = await call<AuthResponse>('/me/avatar', { method: 'DELETE', token })
    return data
  },
  async mediaFromUrl(
    routeId: string,
    url: string,
    token: string,
    opts?: { altText?: string; waypointId?: string },
  ) {
    const { data } = await call<MediaAssetPublic>('/media/from-url', {
      method: 'POST',
      body: {
        route_id: routeId,
        url,
        ...(opts?.altText ? { alt_text: opts.altText } : {}),
        ...(opts?.waypointId ? { waypoint_id: opts.waypointId } : {}),
      },
      token,
    })
    return data
  },
  /** Refresh the route's geo-derived enrich fields (region + activities)
   *  by re-running the deterministic helpers (Tianditu reverse geocode +
   *  peak search + Mapbox water tilequery) against the current GPX blob.
   *  Server writes a revision and bumps the canonical ETag. */
  async regenerateRouteEnrich(routeId: string, token: string, ifMatch: string) {
    const { data, etag } = await call<RoutePublic>(`/ai/route/${routeId}/regenerate-enrich`, {
      method: 'POST',
      token,
      ifMatch,
    })
    return { route: data, etag }
  },
  async deleteMedia(routeId: string, id: string, token: string) {
    await call<void>(`/media/${routeId}/${id}`, { method: 'DELETE', token })
  },

  async setRouteCover(routeId: string, mediaId: string, token: string, ifMatch: string) {
    const { data, etag } = await call<RoutePublic>(`/routes/${routeId}/cover`, {
      method: 'POST',
      body: { media_id: mediaId },
      token,
      ifMatch,
    })
    return { route: data, etag }
  },
  async clearRouteCover(routeId: string, token: string, ifMatch: string) {
    const { data, etag } = await call<RoutePublic>(`/routes/${routeId}/cover`, {
      method: 'DELETE',
      token,
      ifMatch,
    })
    return { route: data, etag }
  },

  // Edit history: list revisions newest-first, then fetch a
  // single revision's full snapshot for diffing.
  async listRevisions(routeId: string, token?: string | null) {
    const { data } = await call<{ items: RevisionPublic[] }>(`/routes/${routeId}/revisions`, {
      token,
    })
    return data.items
  },
  async getRevisionSnapshot(routeId: string, revId: string, token?: string | null) {
    const { data } = await call<{ revision: RevisionPublic; snapshot: Record<string, unknown> }>(
      `/routes/${routeId}/revisions/${revId}`,
      { token },
    )
    return data
  },
  async revertRoute(routeId: string, revId: string, token: string, ifMatch: string) {
    const { data } = await call<{ route: RoutePublic; revision: RevisionPublic }>(
      `/routes/${routeId}/revert/${revId}`,
      { method: 'POST', token, ifMatch },
    )
    return data
  },

  async listComments(routeId: string, anchor?: CommentAnchor | null) {
    const qs = anchor ? `?anchor=${encodeURIComponent(anchor)}` : ''
    const { data } = await call<Comment[]>(`/routes/${routeId}/comments${qs}`)
    return data
  },
  async createComment(
    routeId: string,
    token: string,
    body: { anchor: CommentAnchor | null; body_md: string },
  ) {
    const { data } = await call<Comment>(`/routes/${routeId}/comments`, {
      method: 'POST',
      body,
      token,
    })
    return data
  },
  async resolveComment(commentId: string, token: string) {
    const { data } = await call<Comment>(`/comments/${commentId}/resolve`, {
      method: 'POST',
      token,
    })
    return data
  },
  async addWatch(targetId: string, token: string) {
    const { data } = await call<Watch>('/watches', {
      method: 'POST',
      body: { target_type: 'route', target_id: targetId },
      token,
    })
    return data
  },
  async deleteWatch(targetId: string, token: string) {
    await call<void>(`/watches/${targetId}`, { method: 'DELETE', token })
  },
  async listWatches(token: string) {
    const { data } = await call<Watch[]>('/me/watches', { token })
    return data
  },

  async getInbox(
    token: string,
    options: { notificationLimit?: number; notificationCursor?: string } = {},
  ) {
    const params = new URLSearchParams()
    if (options.notificationLimit)
      params.set('notification_limit', String(options.notificationLimit))
    if (options.notificationCursor) params.set('notification_cursor', options.notificationCursor)
    const query = params.size > 0 ? `?${params.toString()}` : ''
    const { data } = await call<InboxResponse>(`/me/inbox${query}`, { token })
    return data
  },
  async getInboxUnreadCount(token: string) {
    const { data } = await call<InboxUnreadCountResponse>('/me/inbox/unread-count', { token })
    return data.unread_system_notification_count
  },
  async markInboxSeen(token: string, inbox: InboxResponse) {
    const body: InboxSeenRequest = {
      watched_changes: inbox.watched_changes.map((item) => ({
        route_id: item.route.id,
        new_etag: item.new_etag,
      })),
      saved_search_hits: inbox.saved_search_hits.map((item) => ({
        search_id: item.search_id,
        matched_at: item.matched_at,
      })),
      system_notification_ids: [],
    }
    const { data } = await call<{ ok: true }>('/me/inbox/seen', { method: 'POST', token, body })
    return data
  },
  async markSystemNotificationsSeen(token: string, ids: string[]) {
    const body: InboxSeenRequest = {
      watched_changes: [],
      saved_search_hits: [],
      system_notification_ids: ids,
    }
    const { data } = await call<{ ok: true }>('/me/inbox/seen', { method: 'POST', token, body })
    return data
  },
  async createSavedSearch(name: string, query: Record<string, string>, token: string) {
    const { data } = await call<SavedSearch>('/me/saved-searches', {
      method: 'POST',
      body: { name, query },
      token,
    })
    return data
  },
  async listMeContributions(token: string) {
    const { data } = await call<ContributionListResponse>('/me/contributions', { token })
    return Array.isArray(data) ? data : data.items
  },
  async listMyRoutes(token: string, status?: MyRouteStatus) {
    const qs = status ? `?status=${status}` : ''
    const { data } = await call<{ items: RouteListItem[] }>(`/me/routes${qs}`, { token })
    return data.items
  },
  async listMyProposals(token: string) {
    const { data } = await call<{ items: Array<ProposalPublic & { route_title?: string }> }>(
      '/me/proposals',
      { token },
    )
    return data.items
  },
  async listRouteProposals(
    routeId: string,
    status?: 'open' | 'approved' | 'rejected' | 'stale' | 'expired' | 'withdrawn',
    token?: string | null,
  ) {
    const qs = status ? `?status=${encodeURIComponent(status)}` : ''
    const { data } = await call<{ items: ProposalPublic[] }>(
      `/routes/${encodeURIComponent(routeId)}/proposals${qs}`,
      { token },
    )
    return data.items
  },
  async createRouteProposal(routeId: string, body: ProposalCreateRequest, token: string) {
    const { data } = await call<ProposalPublic>(
      `/routes/${encodeURIComponent(routeId)}/proposals`,
      {
        method: 'POST',
        body,
        token,
      },
    )
    return data
  },
  async getProposal(id: string) {
    const { data } = await call<ProposalPublic>(`/proposals/${encodeURIComponent(id)}`)
    return data
  },
  async patchProposal(id: string, patch: Record<string, unknown>, token: string) {
    const { data } = await call<ProposalPublic>(`/proposals/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: patch,
      token,
    })
    return data
  },
  async stageProposalGeometry(id: string, file: File, token: string) {
    const fd = new FormData()
    fd.append('file', file)
    const { data } = await call<ProposalPublic>(`/proposals/${encodeURIComponent(id)}/geometry`, {
      method: 'POST',
      formData: fd,
      token,
    })
    return data
  },
  async stageProposalMedia(id: string, file: File, token: string) {
    const fd = new FormData()
    fd.append('file', file)
    const { data } = await call<{ id: string }>(`/proposals/${encodeURIComponent(id)}/media`, {
      method: 'POST',
      formData: fd,
      token,
    })
    return data
  },
  async unstageProposalMedia(id: string, mediaId: string, token: string) {
    await call<void>(`/proposals/${encodeURIComponent(id)}/media/${encodeURIComponent(mediaId)}`, {
      method: 'DELETE',
      token,
    })
  },
  async approveProposal(id: string, token: string) {
    const { data } = await call<{ route: RoutePublic; proposal: ProposalPublic }>(
      `/proposals/${encodeURIComponent(id)}/approve`,
      {
        method: 'POST',
        token,
      },
    )
    return data
  },
  async rejectProposal(id: string, token: string, body?: ProposalRejectRequest) {
    const { data } = await call<ProposalPublic>(`/proposals/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      body: body ?? {},
      token,
    })
    return data
  },
  async withdrawProposal(id: string, token: string) {
    const { data } = await call<ProposalPublic>(`/proposals/${encodeURIComponent(id)}/withdraw`, {
      method: 'POST',
      token,
    })
    return data
  },

  async fetchWeather(lat: number, lon: number) {
    const { data } = await call<import('@xingroute/schema').WeatherResponse>(
      `/weather?lat=${lat}&lon=${lon}`,
    )
    return data
  },
}
